import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers";
import axiosInstance from "../../../../hooks/api";
import { useFormSubmit } from "../../../../hooks/useFormSubmit";
import CopyFromPanel from "./CopyFromPanel";
import { ADULT_COPY_SECTIONS } from "../../../../helper/copyFormSections";

const SACRAMENT_ORDER = ['baptism', 'communion', 'confirmation', 'matrimony'];

const RECEIVED_OPTIONS = [
  { checkKey: "received_baptism",      prefix: "baptism",      label: "_tab_baptism" },
  { checkKey: "received_communion",    prefix: "communion",    label: "_tab_communion" },
  { checkKey: "received_confirmation", prefix: "confirmation", label: "_tab_confirmation" },
];

const SectionHeader = ({ title }) => (
  <h6 style={{ borderBottom: "2px solid #032C65", paddingBottom: "6px", marginBottom: "16px", color: "#032C65", marginTop: "24px" }}>
    {title}
  </h6>
);

const DateInput = ({ name, required, control, error }) => {
  const { t } = useTranslation();
  return (
    <Controller
      control={control}
      name={name}
      rules={required ? { required: t("_err_required") } : {}}
      render={({ field }) => (
        <>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateField
              sx={{ width: "100%" }}
              onChange={(v) => { if (v?.isValid()) field.onChange(v.format("YYYY-MM-DD")); }}
            />
          </LocalizationProvider>
          {error && <div className="invalid-feedback d-block">{error.message}</div>}
        </>
      )}
    />
  );
};

const NasAdultRegForm = ({
  sacramentType,
  birthDate: dobFromGate,
  onSuccess,
  multiFlow = false,
  isFinalStep = false,
  onContinue,
  onSubmitBatch,
  isBatchLoading = false,
  copySources = [],
}) => {
  const { t } = useTranslation();
  const { isLoading, submit } = useFormSubmit();
  const [formDob, setFormDob]     = useState(dobFromGate || null);
  const { register, handleSubmit, watch, reset, control, setValue, formState: { errors } } = useForm({ mode: "onTouched" });

  const civilStatus   = watch("civil_status");
  const isMarriageTab = sacramentType === "matrimony";

  const idx = SACRAMENT_ORDER.indexOf(sacramentType);
  const sacramentsBefore = idx >= 0 ? SACRAMENT_ORDER.slice(0, idx) : [];
  const busy = isLoading || isBatchLoading;

  const onSubmit = async (data) => {
    if (multiFlow) {
      const draft = {
        kind: "adult",
        sacramentType,
        birthDate: formDob || dobFromGate,
        data: { ...data, birth_date: formDob || dobFromGate },
      };
      if (!isFinalStep) {
        if (onContinue) onContinue(draft);
        return;
      }
      if (onSubmitBatch) await onSubmitBatch(draft);
      return;
    }

    const payload = new FormData();
    payload.append("sacrament_type", sacramentType);
    payload.append("form_type", "adult");
    payload.append("full_name", data.full_name);
    payload.append("birth_date", formDob || "");
    payload.append("phone", data.phone);
    payload.append("email", data.email || "");
    payload.append("address_line_1", data.address_line_1);
    payload.append("city", data.city);
    payload.append("state", data.state);
    payload.append("zip", data.zip);
    payload.append("language", data.language);
    const { documents: files, ...rest } = data;
    payload.append("form_data", JSON.stringify({ ...rest, birth_date: formDob }));
    if (files && files.length > 0) {
      Array.from(files).forEach((f) => payload.append("documents[]", f));
    }

    await submit(
      () => axiosInstance.post("/public/sacrament-registrations", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
      {
        successText: t("_registration_confirmation"),
        onSuccess: () => {
          reset();
          setFormDob(null);
          if (onSuccess) onSuccess();
        },
      }
    );
  };

  const submitLabel = multiFlow
    ? (isFinalStep ? t("_submit_all_registrations") : t("_continue_next_person"))
    : t("_submit_registration");


  return (
    <form onSubmit={handleSubmit(onSubmit)} className="sigma_contact-form p-4" style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>

      {multiFlow && (
        <CopyFromPanel
          sources={copySources}
          sections={ADULT_COPY_SECTIONS}
          setValue={setValue}
        />
      )}

      <div
        style={{
          background: "#e8f0fb",
          padding: "14px",
          borderRadius: "6px",
          marginBottom: "16px",
          fontSize: "13px",
          fontWeight: 600,
          color: "#032C65",
          whiteSpace: "pre-line",
        }}
      >
        {t("_fee_re_and_year_end_note")}
      </div>

      {/* ── Datos Personales ── */}
      <SectionHeader title={t("_section_personal_data")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_full_name")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.full_name ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("full_name", { required: t("_err_required") })}
          />
          {errors.full_name && <div className="invalid-feedback">{errors.full_name.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_sex")} <em className="text-danger">*</em></label>
          <select className="form-select border" {...register("sex")}>
            <option value="">—</option>
            <option value="M">M</option>
            <option value="F">F</option>
          </select>
        </div>
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_address_1")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.address_line_1 ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("address_line_1", { required: t("_err_required") })}
          />
          {errors.address_line_1 && <div className="invalid-feedback">{errors.address_line_1.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_apt")}</label>
          <input className="form-control border" {...register("apt")} />
        </div>
        <div className="col-sm-5">
          <label className="form-label fw-semibold">{t("_city")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.city ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("city", { required: t("_err_required") })}
          />
          {errors.city && <div className="invalid-feedback">{errors.city.message}</div>}
        </div>
        <div className="col-sm-3">
          <label className="form-label fw-semibold">{t("_state")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.state ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px", textTransform: "uppercase" }}
            maxLength={2}
            {...register("state", { required: t("_err_required") })}
          />
          {errors.state && <div className="invalid-feedback">{errors.state.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_zip")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.zip ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("zip", { required: t("_err_required") })}
          />
          {errors.zip && <div className="invalid-feedback">{errors.zip.message}</div>}
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_phone")} <em className="text-danger">*</em></label>
          <input
            type="tel"
            className={`form-control border ${errors.phone ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("phone", { required: t("_err_required") })}
          />
          {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_cell")}</label>
          <input type="tel" className="form-control border" {...register("cell")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_email")} <em className="text-danger">*</em></label>
          <input
            type="email"
            className={`form-control border ${errors.email ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("email", {
              required: t("_err_required"),
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("_err_invalid_email") },
            })}
          />
          {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
        </div>
        {/* DOB: for matrimony tab it's editable; for others it comes from the age gate */}
        {isMarriageTab ? (
          <div className="col-sm-6">
            <label className="form-label fw-semibold">{t("_birth_date")}</label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateField
                sx={{ width: "100%" }}
                onChange={(v) => { if (v?.isValid()) setFormDob(v.format("YYYY-MM-DD")); }}
              />
            </LocalizationProvider>
          </div>
        ) : (
          <div className="col-sm-6">
            <label className="form-label fw-semibold">{t("_birth_date")}</label>
            <div className="form-control border bg-light">{dobFromGate || "—"}</div>
          </div>
        )}
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_birth_place")}</label>
          <input className="form-control border" {...register("birth_place")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_school_if_applicable")}</label>
          <input className="form-control border" {...register("school")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_grade_if_applicable")}</label>
          <input className="form-control border" {...register("grade")} />
        </div>
      </div>

      {/* ── Datos Familiares ── */}
      <SectionHeader title={t("_section_family_data")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_father_name")}</label>
          <input className="form-control border" {...register("father_name")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_father_religion")}</label>
          <input className="form-control border" {...register("father_religion")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_mother_name")}</label>
          <input className="form-control border" {...register("mother_name")} />
        </div>
        <div className="col-sm-6">
          <label className="form-label fw-semibold">{t("_mother_religion")}</label>
          <input className="form-control border" {...register("mother_religion")} />
        </div>
      </div>

      {/* ── Estado Civil ── */}
      <SectionHeader title={t("_section_civil_status")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-12">
          <label className="form-label fw-semibold">{t("_civil_status")} <em className="text-danger">*</em></label>
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            {["single", "married", "separated", "divorced"].map((v) => (
              <label key={v}>
                <input
                  type="radio"
                  value={v}
                  style={v === "single" ? { scrollMarginTop: "100px" } : {}}
                  {...(v === "single"
                    ? register("civil_status", { required: t("_err_required") })
                    : register("civil_status")
                  )}
                /> {t(`_civil_${v}`)}
              </label>
            ))}
          </div>
          {errors.civil_status && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.civil_status.message}</div>}
        </div>
        {civilStatus === "married" && (
          <div className="col-sm-6">
            <label className="form-label fw-semibold">{t("_married_by")}</label>
            <select className="form-select border" {...register("married_by")}>
              <option value="">—</option>
              <option value="church">{t("_catholic_church")}</option>
              <option value="civil">{t("_civil")}</option>
            </select>
          </div>
        )}
        {civilStatus === "divorced" && (
          <div className="col-sm-6">
            <label className="form-label fw-semibold">{t("_previously_married_by")}</label>
            <select className="form-select border" {...register("previously_married_by")}>
              <option value="">—</option>
              <option value="church">{t("_catholic_church")}</option>
              <option value="civil">{t("_civil")}</option>
            </select>
          </div>
        )}
      </div>

      {/* ── Sacramentos ── */}
      <SectionHeader title={t("_section_sacraments")} />
      <div className="row g-3 mb-2">
        {RECEIVED_OPTIONS.filter(({ prefix }) => sacramentsBefore.includes(prefix)).map(({ checkKey, prefix, label }) => {
          const checked = watch(checkKey);
          return (
            <div key={checkKey} className="col-sm-12">
              <label>
                <input type="checkbox" {...register(checkKey)} /> {t("_received")} {t(label)}
              </label>
              {checked && (
                <div className="row g-2 mt-1">
                  <div className="col-sm-4">
                    <DateInput name={`${prefix}_date`} control={control} error={errors[`${prefix}_date`]} />
                  </div>
                  <div className="col-sm-8">
                    <input className="form-control border form-control-sm" placeholder={t("_place")} {...register(`${prefix}_place`)} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sacramentType === "confirmation" && (
        <>
          <SectionHeader title={t("_confirmation_sponsor")} />
          <p className="text-muted" style={{ fontSize: "12.5px", marginTop: "-8px" }}>{t("_confirmation_sponsor_note")}</p>
          <div className="row g-3 mb-2">
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_godparents_full_name")} <em className="text-danger">*</em></label>
              <input
                type="text"
                className={`form-control border ${errors.sponsor_name ? "is-invalid" : ""}`}
                style={{ scrollMarginTop: "100px" }}
                {...register("sponsor_name", { required: t("_err_required") })}
              />
              {errors.sponsor_name && <div className="invalid-feedback">{errors.sponsor_name.message}</div>}
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_sponsor_religion")}</label>
              <input className="form-control border" {...register("sponsor_religion")} />
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_phone")}</label>
              <input type="tel" className="form-control border" {...register("sponsor_phone")} />
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_email")}</label>
              <input
                type="email"
                className={`form-control border ${errors.sponsor_email ? "is-invalid" : ""}`}
                {...register("sponsor_email", {
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("_err_invalid_email") },
                })}
              />
              {errors.sponsor_email && <div className="invalid-feedback">{errors.sponsor_email.message}</div>}
            </div>
          </div>
        </>
      )}

      {/* ── Idioma ── */}
      <div className="mb-4">
        <label className="form-label fw-semibold">{t("_preferred_language")} <em className="text-danger">*</em></label>
        <div>
          <label className="me-3">
            <input
              type="radio"
              value="es"
              style={{ scrollMarginTop: "100px" }}
              {...register("language", { required: t("_err_required") })}
            /> {t("_spanish")}
          </label>
          <label><input type="radio" value="en" {...register("language")} /> {t("_english")}</label>
        </div>
        {errors.language && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.language.message}</div>}
      </div>

      {/* ── Firma ── */}
      <SectionHeader title={t("_section_signature")} />
      <div className="row g-3 mb-2">
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_signature")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.signature ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            placeholder={t("_type_full_name")}
            {...register("signature", { required: t("_err_required") })}
          />
          {errors.signature && <div className="invalid-feedback">{errors.signature.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_date")} <em className="text-danger">*</em></label>
          <DateInput name="signature_date" required control={control} error={errors.signature_date} />
        </div>
      </div>

      {/* ── Contactos de Emergencia (3) ── */}
      <SectionHeader title={t("_section_emergency_contacts")} />
      <div className="row g-3 mb-2">
        {[1, 2, 3].map((n) => (
          <React.Fragment key={n}>
            <div className="col-sm-4">
              <label className="form-label fw-semibold">{t("_contact_name")} #{n} {n === 1 && <em className="text-danger">*</em>}</label>
              <input
                className={`form-control border ${n === 1 && errors[`emergency${n}_name`] ? "is-invalid" : ""}`}
                style={{ scrollMarginTop: "100px" }}
                {...(n === 1
                  ? register(`emergency${n}_name`, { required: t("_err_required") })
                  : register(`emergency${n}_name`)
                )}
              />
              {n === 1 && errors[`emergency${n}_name`] && (
                <div className="invalid-feedback">{errors[`emergency${n}_name`].message}</div>
              )}
            </div>
            <div className="col-sm-4">
              <label className="form-label fw-semibold">{t("_relation")} {n === 1 && <em className="text-danger">*</em>}</label>
              <input
                className={`form-control border ${n === 1 && errors[`emergency${n}_relation`] ? "is-invalid" : ""}`}
                style={{ scrollMarginTop: "100px" }}
                {...(n === 1
                  ? register(`emergency${n}_relation`, { required: t("_err_required") })
                  : register(`emergency${n}_relation`)
                )}
              />
              {n === 1 && errors[`emergency${n}_relation`] && (
                <div className="invalid-feedback">{errors[`emergency${n}_relation`].message}</div>
              )}
            </div>
            <div className="col-sm-4">
              <label className="form-label fw-semibold">{t("_phone")} {n === 1 && <em className="text-danger">*</em>}</label>
              <input
                type="tel"
                className={`form-control border ${n === 1 && errors[`emergency${n}_phone`] ? "is-invalid" : ""}`}
                style={{ scrollMarginTop: "100px" }}
                {...(n === 1
                  ? register(`emergency${n}_phone`, { required: t("_err_required") })
                  : register(`emergency${n}_phone`)
                )}
              />
              {n === 1 && errors[`emergency${n}_phone`] && (
                <div className="invalid-feedback">{errors[`emergency${n}_phone`].message}</div>
              )}
            </div>
            <div className="col-sm-6">
              <label className="form-label fw-semibold">{t("_email")}</label>
              <input type="email" className="form-control border" {...register(`emergency${n}_email`)} />
            </div>
            {n === 3 && (
              <div className="col-sm-6">
                <label className="form-label fw-semibold">{t("_cell")}</label>
                <input type="tel" className="form-control border" {...register("emergency3_cell")} />
              </div>
            )}
          </React.Fragment>
        ))}
        <div className="col-sm-12">
          <label style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
            <input
              type="checkbox"
              style={{ marginTop: "3px", scrollMarginTop: "100px" }}
              {...register("emergency_auth", { required: t("_err_required") })}
            />
            <span>{t("_emergency_treatment_auth")}</span>
          </label>
          {errors.emergency_auth && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.emergency_auth.message}</div>}
        </div>
        <div className="col-sm-8">
          <label className="form-label fw-semibold">{t("_signature")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.emergency_signature ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            placeholder={t("_type_full_name")}
            {...register("emergency_signature", { required: t("_err_required") })}
          />
          {errors.emergency_signature && <div className="invalid-feedback">{errors.emergency_signature.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_date")} <em className="text-danger">*</em></label>
          <DateInput name="emergency_signature_date" required control={control} error={errors.emergency_signature_date} />
        </div>
      </div>

      {/* ── Consentimiento de Fotos ── */}
      <SectionHeader title={t("_section_photo_consent")} />
      <div className="mb-3" style={{ background: "#f8f9fa", padding: "12px", borderRadius: "6px", fontSize: "13px", color: "#555" }}>
        {t("_photo_consent_text")}
      </div>
      <div className="row g-3 mb-2">
        <div className="col-sm-12">
          <label className="me-4">
            <input
              type="radio"
              value="yes"
              style={{ scrollMarginTop: "100px" }}
              {...register("photo_consent", { required: t("_err_required") })}
            /> {t("_photo_consent_yes")}
          </label>
          <label>
            <input type="radio" value="no" {...register("photo_consent")} /> {t("_photo_consent_no")}
          </label>
          {errors.photo_consent && <div className="text-danger" style={{ fontSize: "13px" }}>{errors.photo_consent.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_name")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.photo_consent_name ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            {...register("photo_consent_name", { required: t("_err_required") })}
          />
          {errors.photo_consent_name && <div className="invalid-feedback">{errors.photo_consent_name.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_signature")} <em className="text-danger">*</em></label>
          <input
            type="text"
            className={`form-control border ${errors.photo_signature ? "is-invalid" : ""}`}
            style={{ scrollMarginTop: "100px" }}
            placeholder={t("_type_full_name")}
            {...register("photo_signature", { required: t("_err_required") })}
          />
          {errors.photo_signature && <div className="invalid-feedback">{errors.photo_signature.message}</div>}
        </div>
        <div className="col-sm-4">
          <label className="form-label fw-semibold">{t("_date")} <em className="text-danger">*</em></label>
          <DateInput name="photo_signature_date" required control={control} error={errors.photo_signature_date} />
        </div>
      </div>

      {/* ── Donación Informativa ── */}
      <SectionHeader title={t("_section_fee")} />
      <div style={{ background: "#e8f0fb", padding: "14px", borderRadius: "6px", marginBottom: "16px", fontSize: "13px" }}>
        <div><strong>$70.00</strong> — 1 {t("_adult")}</div>
        <div><strong>$120.00</strong> — 2 {t("_adults")}</div>
        <div><strong>$150.00</strong> — 3 {t("_adults")}</div>
        <hr style={{ margin: "8px 0" }} />
        <div className="text-muted">{t("_fee_adult_special_note")}</div>
        <div className="text-muted">{t("_fee_nonrefundable")}</div>
        <div style={{ fontWeight: 600, color: "#032C65", whiteSpace: "pre-line" }}>
          {t("_fee_re_and_year_end_note")}
        </div>
      </div>

      {/* ── Documentos ── */}
      <SectionHeader title={t("_section_documents")} />
      <div className="mb-4">
        <label className="form-label fw-semibold">{t("_attach_documents")}</label>
        <input
          type="file"
          className="form-control border"
          multiple
          accept="application/pdf,image/*"
          {...register("documents")}
        />
        <small className="text-muted">{t("_accepted_formats")}: PDF, JPG, PNG. Max 10MB {t("_per_file")}.</small>
      </div>

      {/* ── Comentarios ── */}
      <div className="mb-4">
        <label className="form-label fw-semibold">{t("_comments")}</label>
        <textarea className="form-control border" rows={3} {...register("comments")} />
      </div>

      {/* ── Submit ── */}
      <div className="d-grid">
        <button
          type="submit"
          disabled={busy}
          className={busy ? "sigma_btn-custom" : "sigma_btn-custom light"}
          style={{ backgroundColor: busy ? "grey" : "", padding: "14px", fontSize: "16px" }}
        >
          {busy ? "..." : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default NasAdultRegForm;
